import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import api from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import OnboardingShell from "@/pages/onboarding/OnboardingShell";
import { PrimaryButton, ErrorBox } from "@/components/AuthKit";

/**
 * ProfilePhoto — 1:1 port of iOS `src/app/(onboarding)/photo.tsx`.
 * Circular 160×160 avatar drop zone (dashed border when empty, brand-purple
 * ring when filled), 8MB max, JPG/PNG/WEBP. Two-button footer:
 * "Skip for now" (secondary) + "Continue" (primary rainbow).
 */
export default function ProfilePhoto() {
  const navigate = useNavigate();
  const { refresh, apiError } = useAuth();
  const inputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const pick = () => inputRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      setErr("Please choose an image under 8MB.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setErr("");
  };

  const submit = async () => {
    if (!file) return setErr("Please choose a photo.");
    setLoading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("image", file);
      await api.post(EP.profilePhoto, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded.");
      await refresh();
      navigate("/onboarding/interests");
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingShell
      step="photo"
      title="Add a profile photo"
      subtitle="A clear photo helps people recognise you."
      testId="onboarding-photo-screen"
    >
      <div className="flex flex-col items-center">
        {/* 160×160 circular drop zone */}
        <button
          type="button"
          onClick={pick}
          className={`w-40 h-40 rounded-full overflow-hidden flex items-center justify-center transition-colors ${
            preview
              ? "border-2 border-solid border-[#7638FA] p-1 bg-transparent"
              : "border-2 border-dashed border-border bg-secondary"
          }`}
          data-testid="onboard-photo-drop"
        >
          {preview ? (
            <img
              src={preview}
              alt="preview"
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Camera size={30} strokeWidth={1.5} />
              <span
                className="text-[12px] font-bold uppercase"
                style={{ letterSpacing: "1.5px" }}
              >
                UPLOAD
              </span>
            </div>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={onFile}
          className="hidden"
          data-testid="onboard-photo-input"
        />
        <p className="mt-3.5 text-xs text-muted-foreground">
          JPG, PNG or WEBP · up to 8MB
        </p>

        {err && (
          <div className="mt-5 w-full">
            <ErrorBox testId="onboard-photo-error">{err}</ErrorBox>
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-9">
        <button
          type="button"
          onClick={() => navigate("/onboarding/interests")}
          className="flex-1 h-[52px] rounded-2xl bg-secondary text-foreground text-[16px] font-bold border border-border hover:opacity-90 active:opacity-85 transition-all"
          data-testid="onboard-photo-skip"
        >
          Skip for now
        </button>
        <PrimaryButton
          label="Continue"
          onClick={submit}
          loading={loading}
          disabled={!file}
          className="flex-1"
          testId="onboard-photo-submit"
        />
      </div>
    </OnboardingShell>
  );
}
